package org.gstechnovos.jexonplastech.sections.contact.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.contact.styles.ContactFormContainerStyle
import org.gstechnovos.jexonplastech.sections.contact.styles.InputStyle
import org.gstechnovos.jexonplastech.sections.contact.styles.SubmitButtonStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.util.LocalBreakpoint
import org.gstechnovos.jexonplastech.util.Res
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

// ==========================================
// CMS DATA MODEL
// ==========================================

data class ContactFormData(
    val headline: String, val description: String, val submitText: String
)

val defaultContactFormData = ContactFormData(
    headline = "Send Us a Message",
    description = "Fill out the form below with your inquiry, and our B2B sales team will get back to you within 24 hours.",
    submitText = "Submit Inquiry"
)

// ==========================================
// COMPONENT
// ==========================================
/**
 *
 * Text Constraints:
 * Headline: Renders at 36px on desktop and 28px on mobile.
 * Limit to 3–5 words (e.g., "Send Us a Message").
 *
 * Description: Renders at 16px. Limit to 1–3 sentences to introduce
 * the form without pushing the actual input fields too far down the page.
 *
 * Submit Button Text: Limit to 2–4 words (e.g., "Submit Inquiry").
 *
 * Form Input Layout (For your awareness):
 * Top Row: "Full Name" and "Company Name" sit side-by-side on desktop.
 *
 * Middle Row: "Email Address" and "Phone Number" sit side-by-side on desktop.
 *
 * Text Area (Message): This spans the full width of the form and is hardcoded to a height of 150px.
 *
 */
@Composable
fun ContactFormSection(data: ContactFormData = defaultContactFormData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    // --- NEW: Shared modifier for inputs to ensure visible borders and rounded corners ---
    val sharedInputModifier = InputStyle.toModifier().borderRadius(8.px).border(
        width = 1.px, style = LineStyle.Solid, color = palette.custom.darkText.toRgb().copyf(alpha = 0.3f)
    )

    Column(
        modifier = ContactFormContainerStyle.toModifier().padding(topBottom = 50.px, leftRight = 5.percent),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().maxWidth(800.px), horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Header Text
            SpanText(
                text = data.headline,
                modifier = Modifier.color(palette.brand.primaryBlue).fontSize(if (isDesktop) 36.px else 28.px)
                    .fontWeight(FontWeight.Bold).margin(bottom = 16.px).textAlign(TextAlign.Center)
            )

            SpanText(
                text = data.description,
                modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.7f)).fontSize(16.px)
                    .lineHeight(1.6).margin(bottom = 40.px).textAlign(TextAlign.Center)
            )

            // The Form
            Form(
                action = "mailto:sales@jexonplastech.com", attrs = Modifier.fillMaxWidth().toAttrs()
            ) {
                // Top Row: Name and Company
                SimpleGrid(
                    numColumns(1, md = 2), modifier = Modifier.fillMaxWidth().gap(20.px).margin(bottom = 20.px)
                ) {
                    Input(
                        type = InputType.Text,
                        // Applied shared modifier here
                        attrs = sharedInputModifier.toAttrs { attr("placeholder", "Full Name") })
                    Input(
                        type = InputType.Text,
                        // Applied shared modifier here
                        attrs = sharedInputModifier.toAttrs { attr("placeholder", "Company Name") })
                }

                // Middle Row: Email and Phone
                SimpleGrid(
                    numColumns(1, md = 2), modifier = Modifier.fillMaxWidth().gap(20.px).margin(bottom = 20.px)
                ) {
                    Input(
                        type = InputType.Email,
                        // Applied shared modifier here
                        attrs = sharedInputModifier.toAttrs { attr("placeholder", "Email Address") })
                    Input(
                        type = InputType.Tel,
                        // Applied shared modifier here
                        attrs = sharedInputModifier.toAttrs { attr("placeholder", "Phone Number") })
                }

                // Bottom Row: Message
                TextArea(
                    // Applied shared modifier here
                    attrs = sharedInputModifier.height(150.px).margin(bottom = 30.px)
                        .toAttrs { attr("placeholder", "Order Details, Custom Requirements, or General Inquiry...") })

                // Submit Button
                Button(
                    // Applied rounded corners to the button
                    attrs = SubmitButtonStyle.toModifier().borderRadius(8.px).toAttrs {
                        attr("type", "submit")
                    }) {
                    Text(data.submitText)
                }
            }
        }
    }
}